import { memo, useState, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Search, Eye, Bell } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ROUTES } from '@/app/routes'
import { TopicSwitcher } from '@/features/topics/ui/TopicSwitcher'
import { useAuth } from '@/features/auth/state/useAuth'

const ICON_SIZE = 20
const ICON_STROKE = 2

type TopBarProps = {
  onSearch?: (query: string) => void
  unreadNotifications?: number
  className?: string
}

const iconButtonStyle =
  'relative flex h-11 w-11 cursor-pointer items-center justify-center rounded-lg border-none bg-transparent text-foreground transition-colors hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring'

export const TopBar = memo(function TopBar({
  onSearch,
  unreadNotifications = 0,
  className,
}: TopBarProps) {
  const { t } = useTranslation()
  const { t: tTopbar } = useTranslation('topbar')
  const { isAuthenticated } = useAuth()
  const [notificationsOpen, setNotificationsOpen] = useState(false)
  const notificationsRef = useRef<HTMLDivElement>(null)
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    if (!notificationsOpen) return
    const handleClickOutside = (e: MouseEvent) => {
      if (
        notificationsRef.current &&
        !notificationsRef.current.contains(e.target as Node)
      ) {
        setNotificationsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [notificationsOpen])

  return (
    <header
      className={cn(
        'print-hide flex h-16 w-full items-center gap-4 border-b border-border bg-background px-6',
        className,
      )}
    >
      <div className="flex shrink-0 items-center gap-3">
        <Link
          to={ROUTES.app.root}
          className="flex min-h-[44px] items-center gap-2 text-foreground no-underline"
        >
          <span className="flex shrink-0 items-center justify-center">
            <Eye size={20} strokeWidth={ICON_STROKE} className="text-foreground" />
          </span>
          <span className="text-xl font-semibold text-foreground">Omni Sight</span>
        </Link>
        <TopicSwitcher />
      </div>

      <div className="mx-auto flex-1">
        <div className="mx-auto w-full max-w-[680px]">
          <div className="relative w-full">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="search"
              placeholder={t('topbar.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value)
                onSearch?.(e.target.value)
              }}
              className="flex h-9 w-full rounded-full border border-input bg-muted/30 px-3 py-1 pl-9 text-sm text-foreground backdrop-blur-md transition-all placeholder:text-muted-foreground/60 focus-visible:border-primary/50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary/20"
            />
          </div>
        </div>
      </div>

      <div className="flex shrink-0 items-center justify-end">
        {isAuthenticated ? (
          <div ref={notificationsRef} className="relative">
            <button
              type="button"
              onClick={() => setNotificationsOpen((o) => !o)}
              className={iconButtonStyle}
              aria-label={t('topbar.notifications')}
            >
              <Bell size={ICON_SIZE} strokeWidth={ICON_STROKE} />
              {unreadNotifications > 0 && (
                <span
                  className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-destructive ring-2 ring-background"
                  aria-hidden
                />
              )}
            </button>

            {notificationsOpen && (
              <div className="absolute right-0 top-full z-50 mt-2 min-w-[280px] rounded-lg border border-border bg-popover p-4 shadow-lg">
                <div className="mb-2 text-sm font-semibold text-foreground">
                  {t('topbar.notifications')}
                </div>
                <div className="p-6 text-center text-sm text-muted-foreground">
                  {t('topbar.noNotifications')}
                </div>
              </div>
            )}
          </div>
        ) : (
          <Link
            to={ROUTES.auth.login}
            className="flex h-9 items-center rounded-lg border border-border bg-transparent px-4 text-sm font-medium text-foreground no-underline transition-colors hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring"
          >
            {tTopbar('auth.login')}
          </Link>
        )}
      </div>
    </header>
  )
})
